import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

import usptoApi from '../../../../api/data-sections/usptoApi';
import { useSystemError } from '../../../../components/Error/SystemErrorContext';
import { UsptoApplicationModel, UsptoApplicationClaimModel } from '../../../../models/data-sections/uspto/applicationModel';
import { ResponseErrorModel } from '../../../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../../../models/system/responseSuccessModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../../../utils/errorUtils';

interface ApplicationModalProps {
  searchExecutionId: string;
  selected_application: UsptoApplicationModel;
}

const ApplicationModal: React.FC<ApplicationModalProps> = ({
  searchExecutionId,
  selected_application
}) => {
  const [applicationData, setApplicationData] = useState<UsptoApplicationModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [didErrorOccur, setDidErrorOccur] = useState(false);
  const { triggerSystemError } = useSystemError();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        await fetchApiGetApplicationDetails(
          searchExecutionId,
          selected_application.application_country,
          selected_application.application_doc_number,
          selected_application.application_date,
          new Date(selected_application.__ci_system_search_date_key ?? 0)
        );
      } catch (error) {
        console.error('Error during fetch:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [searchExecutionId, selected_application]);

  const fetchApiGetApplicationDetails = async (
    search_execution_id: string,
    application_country: string,
    application_doc_number: string,
    application_date: string,
    __ci_system_search_date_key: Date
  ): Promise<void> => {
    try {
      const response = await usptoApi.getApplicationDetail(
        search_execution_id,
        application_country,
        application_doc_number,
        application_date,
        __ci_system_search_date_key
      );

      if (response.success) {
        const data = (response as ResponseSuccessModel).data as UsptoApplicationModel[];
        setApplicationData(data?.[0] || null);
      } else {
        handleError(
          mapResponseErrorToSystemError(response as ResponseErrorModel),
          null,
          setDidErrorOccur,
          triggerSystemError
        );
      }
    } catch (error) {
      handleError(
        mapUnknownErrorToSystemError(error, false),
        null,
        setDidErrorOccur,
        triggerSystemError
      );
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gap={1} mb={2}>
        <Typography variant="h6">
          {applicationData?.invention_title || 'USPTO Application'}
        </Typography>
        <Typography variant="subtitle2" color="textSecondary">
          {selected_application.application_country} / {selected_application.application_doc_number} / {selected_application.application_date}
        </Typography>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      ) : didErrorOccur || !applicationData ? (
        <Typography color="error">Failed to load application details.</Typography>
      ) : (
        <Box mt={2}>
          <Typography><strong>Application Date:</strong> {applicationData.application_date || 'N/A'}</Typography>

          {applicationData.applicants && applicationData.applicants.length > 0 && (
            <Typography><strong>Applicants:</strong> {applicationData.applicants.map(a => a?.orgname || '').filter(Boolean).join(', ')}</Typography>
          )}

          {applicationData.abstract_text && (
            <Box mt={2}>
              <Typography variant="h6">Abstract</Typography>
              <Typography paragraph>{applicationData.abstract_text.trim()}</Typography>
            </Box>
          )}

          {applicationData.claims && applicationData.claims.length > 0 && (
            <Box mt={3}>
              <Typography variant="h6">Claims</Typography>
              <Box component="ul" pl={3}>
                {applicationData.claims.flatMap((claim: UsptoApplicationClaimModel) =>
                  (claim.claim_texts || []).map((text, index) => (
                    <li key={`${claim.id || index}-${index}`} style={{ marginBottom: '0.5rem' }}>
                      {text}
                    </li>
                  ))
                )}
              </Box>
            </Box>
          )}

          {applicationData.descriptions && applicationData.descriptions.length > 0 && (
            <Box mt={3}>
              <Typography variant="h6">Description</Typography>
              {applicationData.descriptions.map((desc, index) => (
                <Typography key={index} paragraph>{desc}</Typography>
              ))}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default ApplicationModal;
